import { useCallback, useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { getCurrentUser } from '@/selectors/user.selectors'
import { useReselector } from '@/utils/sharedHooks'
import { useAppDispatch } from '@/utils/typeHelpers'
import { fetchInvoicePaymentUrl } from '@/actions/paymentActions'
import { fetchCheckoutSession } from '@/actions/settings/billingActions'
import { TypographyStyles } from '@/styles'

import { Alert, Button, Card, Link, Text } from '@/ui'
import {
  selectCatchUpBookkeepingIsUnpaid,
  selectCatchUpBookkeepingStatusForCurrentUser,
  selectTrialUserHasUnpaidCatchUpBookkeeping,
} from '@/components/CatchUpBookkeepingStatus/catchUpBookkeepingStatus.selector'
import { fetchUserCatchUpBookkeepingStatus } from '@/components/CatchUpBookkeepingStatus/catchUpBookkeepingStatus.slice'
import { useCatchupBookkeepingPrice } from '@/features/user/signup/payment/hooks'
import { SIGNUP_PATHS } from '@/features/user/signup/helpers'
import { CaseStyle, useTherapistFlag } from '@/utils/launchdarklyHelpers'

const CatchUpBookkeepingUnpaid = () => {
  const dispatch = useAppDispatch()
  const isUnpaidTrialUser = useReselector(
    selectTrialUserHasUnpaidCatchUpBookkeeping
  )
  const catchUpBkStatus = useReselector(
    selectCatchUpBookkeepingStatusForCurrentUser
  )
  const user = useReselector(getCurrentUser)
  const catchupBookkeepingPrice = useCatchupBookkeepingPrice()
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const catchUpBookkeepingIsUnpaid = useReselector(
    selectCatchUpBookkeepingIsUnpaid
  )
  const navigate = useNavigate()
  const replacementText = useTherapistFlag(CaseStyle.lowerCase, true)

  useEffect(() => {
    dispatch(fetchUserCatchUpBookkeepingStatus())
  }, [dispatch])

  useEffect(() => {
    if (
      catchUpBkStatus &&
      !catchUpBookkeepingIsUnpaid &&
      (catchUpBkStatus.paidAt || !catchUpBkStatus.paymentRequired)
    ) {
      navigate('/dashboard')
    }
  }, [catchUpBkStatus, catchUpBookkeepingIsUnpaid, navigate])

  const getPaymentLink = useCallback(async () => {
    if (catchUpBkStatus?.invoiceId) {
      const url = await fetchInvoicePaymentUrl(catchUpBkStatus.invoiceId)(
        dispatch
      )
      return url ?? null
    }
    if (!user?.id || !catchupBookkeepingPrice) {
      return null
    }
    const res = await fetchCheckoutSession({
      userId: user.id,
      lineItems: [
        {
          priceId: catchupBookkeepingPrice.priceId,
          quantity: catchupBookkeepingPrice.monthCount,
        },
      ],
      success_url: {
        url: user.acquisitionSource ? '/dashboard' : SIGNUP_PATHS.success,
      },
      cancel_url: {
        url: `${location.pathname}${location.search}`,
      },
      mode: 'payment',
    })(dispatch)
    return res?.url ?? null
  }, [
    user?.id,
    catchupBookkeepingPrice,
    catchUpBkStatus?.invoiceId,
    dispatch,
    user?.acquisitionSource,
  ])

  const redirectToCheckout = useCallback(async () => {
    setLoading(true)
    const url = await getPaymentLink()
    if (url) {
      window.location.href = url
    } else {
      setError(
        'Could not retrieve payment link. Please refresh the page and try again. If the error persists, please contact support.'
      )
    }
    setLoading(false)
  }, [getPaymentLink])

  return (
    <Card className="flex flex-col items-center justify-center p-16">
      <div className="flex flex-col items-center justify-center gap-4">
        <Text variant={TypographyStyles.H1} className="text-center">
          Almost there: Pay Catch Up Bookkeeping Fee
        </Text>
        {isUnpaidTrialUser && (
          <Text variant={TypographyStyles.BODY_SM} className="text-center my-4">
            Thank you for trying Heard! We&apos;re excited to have you
            officially join our community of financially empowered{' '}
            <span>{replacementText}</span>. During your trial, we began
            organizing your past financial records (this is known as catch up
            bookkeeping) to ensure accurate reporting and tax estimates. To
            re-gain full access to Heard, a one-time catch up bookkeeping fee is
            required. This covers the work done to bring your books up to date.
          </Text>
        )}
        {!isUnpaidTrialUser && (
          <Text variant={TypographyStyles.BODY_SM} className="text-center my-4">
            We&apos;re excited for you to start with Heard! Before onboarding,
            there&apos;s one last step: paying a one-time catch up bookkeeping
            fee. This ensures your financial data and tax estimates are accurate
            from day one.
          </Text>
        )}
        <Button
          disabled={loading}
          loading={loading}
          onClick={redirectToCheckout}
        >
          {isUnpaidTrialUser ? 'Unlock my access' : 'Activate my account'}
        </Button>
        {error && <Alert variant="error">{error}</Alert>}
        <Text variant={TypographyStyles.BODY_SM} className="text-center">
          Questions? We&apos;re here to help:{' '}
          <Link variant="secondary" href="mailto:contact@joinheard.com">
            contact@joinheard.com
          </Link>
          .
        </Text>
      </div>
    </Card>
  )
}

export { CatchUpBookkeepingUnpaid }
